import { TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import { ReactNode } from 'react';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { SBHorizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelM } from '@sorare/core/src/atoms/typography';
import Since from '@sorare/core/src/contexts/intl/Since';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { isType } from '@sorare/core/src/gql';
import useScreenSize from '@sorare/core/src/hooks/device/useScreenSize';
import useFeatureFlags from '@sorare/core/src/hooks/useFeatureFlags';

import ItemUser from 'components/ItemPreview/ItemUser';
import CardProperties from 'components/card';
import { SellCommonCard } from 'components/commonCard/SellCommonCard';
import CardDescription from 'components/token/CardDescription';
import FlexCard from 'components/token/FlexCard';

import { CommonCardPreview_card } from './__generated__/index.graphql';

const MobileCommonCardContainer = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: min-content 1fr;
  width: 100%;
  align-items: start;
  gap: var(--unit);
`;

const MobileCommonCardImgContainer = styled.div`
  width: var(--card-mobile-width);
`;
const MobileCardData = styled(Vertical).attrs({ gap: 0.5 })`
  flex-grow: 1;
  overflow: hidden;
`;

const CardFrame = styled(Vertical).attrs({ gap: 1.5 })`
  position: relative;
  align-items: flex-start;
  border-radius: var(--unit);
`;

const Column = styled(Vertical)`
  width: 100%;
  align-items: flex-start;
`;

const FlexCardWrapper = styled(ButtonBase)`
  width: 100%;
  position: relative;

  &.selectedToDrop {
    & > * {
      opacity: 0.7;
    }
    &:after {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: 20px;
      outline: 3px solid var(--c-rivals-loss);
      background: radial-gradient(
        154.46% 96.82% at 50% 3.18%,
        rgba(255, 78, 34, 0) 70%,
        var(--c-rivals-loss) 100%
      );
      background-blend-mode: hard-light;
    }
  }
`;

const ActionWrapper = styled.div`
  position: absolute;
  right: var(--unit);
  top: var(--unit);
  display: none;
  ${CardFrame}:hover & {
    display: block;
  }
`;

type Props = {
  card: CommonCardPreview_card;
  hideDetails?: boolean;
  action?: ReactNode;
  canSellCommon?: boolean;
  onInfoClick?: () => void;
  onToggleCardToDrop?: (slug: string) => void;
  selectedToDrop?: boolean;
};
const CommonCardPreview = ({
  card,
  hideDetails,
  action,
  canSellCommon = false,
  onInfoClick,
  onToggleCardToDrop,
  selectedToDrop,
}: Props) => {
  const { up: isTablet } = useScreenSize('tablet');
  const { sport } = useSportContext();

  const {
    flags: { useTwoColumnsMarketOnMobile = false },
  } = useFeatureFlags();

  if (!useTwoColumnsMarketOnMobile && !isTablet && !hideDetails) {
    return (
      <MobileCommonCardContainer>
        <MobileCommonCardImgContainer>
          <FlexCardWrapper
            className={classNames({
              selectedToDrop,
            })}
            onClick={() => onToggleCardToDrop?.(card.slug)}
          >
            <FlexCard
              withLink={!(canSellCommon && sport === Sport.FOOTBALL)}
              card={card}
            />
          </FlexCardWrapper>
        </MobileCommonCardImgContainer>
        <MobileCardData>
          <SBHorizontal gap={0}>
            <CardProperties
              card={card}
              cardWithStats={card}
              withTransferMalus={false}
              onInfoClick={onInfoClick}
            />
            {isType(card, 'Card') && card.ownerSince && (
              <Since date={card.ownerSince} />
            )}
          </SBHorizontal>
          <CardDescription
            card={card}
            Details={LabelM}
            detailsColor="var(--c-nd-600)"
          />
          {card.user && <ItemUser item={card.user} />}
        </MobileCardData>
        {canSellCommon && (
          <SellCommonCard
            card={card}
            onSelect={() => onToggleCardToDrop?.(card.slug)}
            selected={selectedToDrop}
          />
        )}
      </MobileCommonCardContainer>
    );
  }
  return (
    <CardFrame>
      <FlexCardWrapper
        className={classNames({
          selectedToDrop,
        })}
        onClick={() => onToggleCardToDrop?.(card.slug)}
      >
        <FlexCard
          withLink={!(canSellCommon && sport === Sport.FOOTBALL)}
          card={card}
        />
      </FlexCardWrapper>
      {action && <ActionWrapper>{action}</ActionWrapper>}
      {!hideDetails && (
        <Column>
          <CardProperties
            card={card}
            cardWithStats={card}
            withTransferMalus={false}
            onInfoClick={onInfoClick}
          />

          {isType(card, 'Card') && card.ownerSince && (
            <Since date={card.ownerSince} />
          )}
          {canSellCommon && (
            <SellCommonCard
              card={card}
              onSelect={() => onToggleCardToDrop?.(card.slug)}
              selected={selectedToDrop}
            />
          )}
        </Column>
      )}
    </CardFrame>
  );
};

CommonCardPreview.fragments = {
  card: gql`
    fragment CommonCardPreview_card on AnyCardInterface {
      slug
      ... on Card {
        slug
        ownerSince
      }
      user {
        slug
        ...ItemUser_user
      }
      ...CardDescription_anyCard
      ...CardProperties_anyCard
      ...CardPropertiesWithStats_anyCard
      ...FlexCard_anyCard
      ...SellCommonCard_anyCard
    }
    ${ItemUser.fragments.user}
    ${FlexCard.fragments.anyCard}
    ${CardProperties.fragments.anyCard}
    ${CardProperties.fragments.anyCardWithStats}
    ${CardDescription.fragments.anyCard}
    ${SellCommonCard.fragments.anyCard}
  ` as TypedDocumentNode<CommonCardPreview_card>,
};
export default CommonCardPreview;
